import { Button } from "@/components/core/buttons/button";
import Indicator from "@/components/core/indicator";
import { IconArrowLeft, IconArrowRight, IconCheck } from "@tabler/icons-react";
import clsx from "clsx";
import { useEffect, useState } from "react";

export type Step = {
	label: string;
	value: string;
	disabled?: boolean;
};

type StepperProps = {
	value: string;
	steps: Step[];
	navigation?: boolean;
	children: React.ReactNode;
	onChange: (stage: string) => void;
};

export function Stepper({ value = "", children, steps, navigation = false, onChange }: StepperProps) {
	const activeStepIndex = steps.findIndex((s) => s.value === value);
	const activeStep = activeStepIndex >= 0 ? activeStepIndex : 0;
	const [maxStepReached, setMaxStepReached] = useState(activeStep);

	useEffect(() => {
		setMaxStepReached((prev) => Math.max(prev, activeStep));
	}, [activeStep]);

	const goToStep = (stepValue: string, index: number) => {
		if (index <= maxStepReached && !steps[index].disabled) {
			onChange(stepValue);
		}
	};

	// Navigate to the next enabled step.
	const goToNextStep = () => {
		let nextStep = activeStep + 1;
		while (nextStep < steps.length && steps[nextStep].disabled) {
			nextStep++;
		}
		if (nextStep < steps.length) {
			onChange(steps[nextStep].value);
			setMaxStepReached((prev) => Math.max(prev, nextStep));
		}
	};

	// Navigate to the previous enabled step.
	const goToPrevStep = () => {
		let prevStep = activeStep - 1;
		while (prevStep >= 0 && steps[prevStep].disabled) {
			prevStep--;
		}
		if (prevStep >= 0) {
			onChange(steps[prevStep].value);
		}
	};

	return (
		<div className="flex flex-col w-full gap-6 mt-6">
			<div className="flex flex-row w-full">
				{steps.map((step, index) => {
					const isActive = step.value === value;
					const isCompleted = index < maxStepReached;
					const isDisabled = step.disabled;
					const isMaxStep = index === maxStepReached;

					return (
						<button
							type="button"
							key={index}
							className={clsx(
								"group inline-flex items-center flex-col w-full gap-1.5",
								index <= maxStepReached && !isDisabled ? "cursor-pointer" : "cursor-not-allowed",
							)}
							onClick={() => goToStep(step.value, index)}
							disabled={index > maxStepReached || isDisabled}
						>
							{/* Step indicator */}
							<div
								className={clsx("inline-flex justify-center items-center w-[24px] h-[24px] border-2 rounded-full transition-all", {
									"border-green-600 bg-green-600 text-white": isCompleted,
									"border-primary-600 bg-primary-600 text-white": isActive,
									"border-border bg-background text-foreground/50": !isActive && !isCompleted,
									"opacity-50 cursor-not-allowed": isDisabled,
									"bg-transparent": isMaxStep,
								})}
							>
								{isMaxStep ? (
									<Indicator size="lg" />
								) : isCompleted ? (
									<IconCheck size={14} stroke={3} />
								) : (
									<span className="text-sm font-semibold">{index + 1}</span>
								)}
							</div>

							{/* Step label */}
							<div
								className={clsx("flex justify-center w-full py-2 rounded-b-md border-t-2 transition-all", {
									"border-green-600": isCompleted,
									"border-primary-600 bg-primary-100/50": isActive,
									"border-border": !isActive && !isCompleted,
								})}
							>
								<span
									className={clsx("text-sm font-semibold transition-all", {
										"text-primary-600": isActive,
										"text-foreground/50 group-hover:text-foreground": !isActive,
									})}
								>
									{step.label}
								</span>
							</div>
						</button>
					);
				})}
			</div>

			{/* Step content */}
			{children}

			{/* Optional navigation buttons */}
			{navigation && (
				<div className="flex justify-between">
					<Button onClick={goToPrevStep} type="button" disabled={activeStep === 0}>
						<IconArrowLeft size={18} stroke={2} />
						<span>Back</span>
					</Button>
					<Button onClick={goToNextStep} type="button" disabled={activeStep >= steps.length - 1}>
						<span>Next</span>
						<IconArrowRight size={18} stroke={2} />
					</Button>
				</div>
			)}
		</div>
	);
}
